/** SUNTRONIX ONE — Approvals Inbox (full page).
 *
 *  Full list of pending + recently-decided approval requests.  Same
 *  role-scoped filtering as the ONE Bar chip.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/app/PageHeader";
import Section from "@/components/app/Section";
import { Button } from "@/components/ui/button";
import { RefreshCcw, Check, X, ClipboardCheck } from "lucide-react";
import SlaCountdown from "@/components/app/SlaCountdown";

const APPROVER_ROLES = new Set([
  "super_admin", "suntronix_ops_admin", "operations_admin",
  "customer_finance", "customer_global_admin", "customer_operations_admin",
]);

const TABS = [
  { key: "pending",  label: "Pending"  },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
];

export default function ApprovalsInbox() {
  const { user } = useAuth();
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deciding, setDeciding] = useState(null);

  const canSee = !!user && APPROVER_ROLES.has(user.role);
  const wideOpen = user && (user.role === "super_admin"
                              || user.role === "suntronix_ops_admin");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/approvals",
        { params: { status: tab, limit: 200 } });
      const filtered = (data.items || []).filter((it) =>
        wideOpen ? true : it.approver_role === user?.role);
      setItems(filtered);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load approvals");
    } finally { setLoading(false); }
  }, [tab, wideOpen, user]);

  useEffect(() => { if (canSee) load(); }, [canSee, load]);

  const decide = async (id, decision) => {
    setDeciding(id);
    try {
      await api.post(`/approvals/${id}/decide`, { decision });
      toast.success(decision === "approve" ? "Approved" : "Rejected");
      setItems((xs) => xs.filter((x) => x.id !== id));
    } catch (e) {
      toast.error(e.response?.data?.detail || `Could not ${decision}`);
    } finally { setDeciding(null); }
  };

  if (!canSee) {
    return (
      <div className="max-w-3xl mx-auto py-16 text-center text-slate-500 text-[13px]"
        data-testid="approvals-inbox-forbidden">
        You are not currently assigned as an approver for this workspace.
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-[1200px]" data-testid="approvals-inbox-page">
      <PageHeader
        eyebrow="Workspace · Governance"
        title="Approvals inbox"
        context="Every action that hits the Approval Matrix — repairs, recycles, grade-B reuse, cross-border, procurement — waits here for a decision."
        actions={
          <Button variant="outline" onClick={load}
            data-testid="approvals-refresh">
            <RefreshCcw className="h-3.5 w-3.5 mr-1.5" /> Refresh
          </Button>
        }
        testId="approvals-inbox-header"
      />

      <div className="border-b border-[color:var(--st-border)] flex gap-1">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            data-testid={`approvals-tab-${t.key}`}
            className={"px-3 py-2 text-[12px] font-medium border-b-2 -mb-px " +
              (tab === t.key
                ? "border-slate-900 text-slate-900"
                : "border-transparent text-slate-500 hover:text-slate-700")}>
            {t.label}
          </button>
        ))}
      </div>

      <Section title={`${items.length} ${tab}`} testId="approvals-inbox-list">
        <div className="st-panel overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">Action</th>
                <th className="text-left px-3 py-2">Amount</th>
                <th className="text-left px-3 py-2">Reference</th>
                <th className="text-left px-3 py-2">Reason</th>
                <th className="text-left px-3 py-2">Requested by</th>
                <th className="text-left px-3 py-2">When</th>
                {tab === "pending" && (
                  <th className="text-left px-3 py-2">SLA</th>
                )}
                {tab === "pending" && (
                  <th className="text-right px-3 py-2">Decision</th>
                )}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={tab === "pending" ? 8 : 6}
                  className="text-slate-400 text-center py-6">Loading…</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={tab === "pending" ? 8 : 6}
                  className="text-slate-500 text-center py-8">
                  <ClipboardCheck className="h-6 w-6 mx-auto mb-2 text-slate-300" />
                  Nothing to show in {tab}.
                </td></tr>
              )}
              {items.map((it) => (
                <tr key={it.id} className="border-t border-slate-100"
                  data-testid={`approval-row-${it.id}`}>
                  <td className="px-3 py-2 capitalize font-medium">
                    {(it.action_type || "").replace(/_/g, " ")}
                  </td>
                  <td className="px-3 py-2 font-mono">
                    {it.amount != null ? it.amount : "—"}
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px]">
                    {it.reference || "—"}
                  </td>
                  <td className="px-3 py-2 text-slate-700">{it.reason || "—"}</td>
                  <td className="px-3 py-2 text-[11px]">{it.requested_by || "—"}</td>
                  <td className="px-3 py-2 font-mono text-[11px] text-slate-600">
                    {it.requested_at ? new Date(it.requested_at).toLocaleString() : "—"}
                  </td>
                  {tab === "pending" && (
                    <td className="px-3 py-2">
                      <SlaCountdown
                        requestedAt={it.requested_at}
                        slaHours={it.escalation_hours || 24}
                        testId={`approval-sla-${it.id}`} />
                    </td>
                  )}
                  {tab === "pending" && (
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex gap-1">
                        <Button size="sm" disabled={deciding === it.id}
                          onClick={() => decide(it.id, "approve")}
                          data-testid={`row-approve-${it.id}`}>
                          <Check className="h-3 w-3 mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="outline"
                          disabled={deciding === it.id}
                          onClick={() => decide(it.id, "reject")}
                          data-testid={`row-reject-${it.id}`}>
                          <X className="h-3 w-3 mr-1" /> Reject
                        </Button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
}
